/**
 * dm-reveal — shared scroll-reveal for dm-pdp-* sections.
 *
 * Figma 213:672: 15% threshold, opacity 0→1 + translateY(30px)→0, 500ms
 * cubic-bezier(0.15,0.5,0.25,1), fires once.
 *
 * FAIL-SAFE BY DESIGN. The CSS only hides `[data-dm-reveal="ready"]`, a state this script
 * sets. If the script never runs, nothing is hidden — content cannot be stranded
 * invisible by a JS failure. Elements already on screen at load skip straight to "shown",
 * so above-the-fold content never flashes.
 */
const THRESHOLD = 0.15;

function reveal() {
  const targets = document.querySelectorAll('[data-dm-reveal]:not([data-dm-reveal="shown"])');
  if (!targets.length) return;

  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  if (reduced || typeof IntersectionObserver === 'undefined') {
    for (const target of targets) target.setAttribute('data-dm-reveal', 'shown');
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.setAttribute('data-dm-reveal', 'shown');
        observer.unobserve(entry.target);
      }
    },
    { threshold: THRESHOLD }
  );

  for (const target of targets) {
    const box = target.getBoundingClientRect();
    const onScreen = box.top < window.innerHeight && box.bottom > 0;

    // Already visible: show it now rather than hiding it just to fade it back in.
    if (onScreen) {
      target.setAttribute('data-dm-reveal', 'shown');
      continue;
    }

    target.setAttribute('data-dm-reveal', 'ready');
    observer.observe(target);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', reveal, { once: true });
} else {
  reveal();
}

// Sections added or reordered in the theme editor need re-scanning.
document.addEventListener('shopify:section:load', reveal);

// Shared scroll-to affordance: any element with data-dm-scroll-to="<selector>" scrolls
// the first match into view (the reviews CTA and sticky bar use it).
document.addEventListener('click', (event) => {
  const trigger = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-dm-scroll-to]');
  if (!(trigger instanceof HTMLElement)) return;

  const target = document.querySelector(trigger.dataset.dmScrollTo || '');
  if (!target) return;

  event.preventDefault();
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  // block: 'start' for a section (the hero stars -> #reviews, landing under the header via
  // scroll-margin-top); 'center' stays the default for a single control such as the form.
  const block = trigger.dataset.dmScrollBlock === 'start' ? 'start' : 'center';
  target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block });
  // Hash for back/forward and sharing, without the jump a real fragment navigation would cause.
  if (trigger instanceof HTMLAnchorElement && trigger.hash) history.replaceState(null, '', trigger.hash);
});
